import React from "react";
import { motion } from "framer-motion";
import "./Services.css";
import SEO from "../components/SEO";

const fadeIn = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

function Services() {
  return (
    <>
      <SEO />
      <main className="services-page">
        <section className="services-hero">
          <div className="container">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              className="services-hero-content"
            >
              <h1>Systems That Support the Business, Not Just the Website</h1>
              <p className="hero-subtitle">
                WaveTech is a technical partner for online businesses. We design and run the systems around your store, your operations and your data.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="service-detail-section">
          <div className="container">
            <h2>Shopify Storefronts & Theme Systems</h2>
            <p className="service-description">
              Opinionated storefronts and theme systems built around your catalog, merchandising,
              and operations, not a generic template.
            </p>
            <p className="service-who">
              <strong>Who it&apos;s for:</strong> Shopify brands that have outgrown a starter theme,
              are fighting with fragile customizations, or need a more structured front-end and
              section system.
            </p>
            <h3>Outcomes</h3>
            <ul className="service-deliverables">
              <li>Clean, maintainable theme architecture with clear sections and blocks.</li>
              <li>Performance-focused storefront tuned for your product catalog.</li>
              <li>Room to iterate on merchandising without breaking the underlying system.</li>
            </ul>
            <div className="service-tech">
              <p>
                <strong>Typical timeline:</strong> 4–8 weeks depending on scope and migration
                needs.
              </p>
            </div>
          </div>
        </section>

        <section className="service-detail-section alt-bg">
          <div className="container">
            <h2>Custom Web Apps & Dashboards</h2>
            <p className="service-description">
              Internal tools and dashboards that give operators real-time visibility into what&apos;s
              actually happening across orders, inventory, marketing, and fulfillment.
            </p>
            <p className="service-who">
              <strong>Who it&apos;s for:</strong> Teams that are running the business out of
              spreadsheets, scattered reports, or brittle legacy tools and need a single, trusted
              surface.
            </p>
            <h3>Outcomes</h3>
            <ul className="service-deliverables">
              <li>Single source of truth dashboards for key metrics and operations.</li>
              <li>Role-specific views for operators, leadership, and finance.</li>
              <li>Clear data models and ownership so future changes are safe.</li>
            </ul>
            <div className="service-tech">
              <p>
                <strong>Typical timeline:</strong> 6–10 weeks depending on integrations and data
                complexity.
              </p>
            </div>
          </div>
        </section>

        <section className="service-detail-section">
          <div className="container">
            <h2>Automations & Integrations</h2>
            <p className="service-description">
              Infrastructure that connects Shopify, ERPs, CRMs, warehouses, and analytics, with
              observability built in so you can trust the data.
            </p>
            <p className="service-who">
              <strong>Who it&apos;s for:</strong> Businesses that are manually moving CSVs or
              fighting brittle third-party connectors and need stable, explainable automation.
            </p>
            <h3>Outcomes</h3>
            <ul className="service-deliverables">
              <li>Reliable syncs between systems with clear failure handling.</li>
              <li>Event logs and dashboards so you can see what ran, when, and why.</li>
              <li>Reduction in manual ops work and fewer &quot;silent failures.&quot;</li>
            </ul>
            <div className="service-tech">
              <p>
                <strong>Typical timeline:</strong> 3–8 weeks depending on systems and volume.
              </p>
            </div>
          </div>
        </section>

        <section className="service-detail-section alt-bg">
          <div className="container">
            <h2>SaaS & Shopify Apps</h2>
            <p className="service-description">
              Net-new products from the ground up: app architecture, UX, Shopify App Store
              requirements, and long-term maintenance.
            </p>
            <p className="service-who">
              <strong>Who it&apos;s for:</strong> Teams that want to launch or evolve a product in
              the Shopify ecosystem or adjacent SaaS, and need an engineering partner who has done
              it before.
            </p>
            <h3>Outcomes</h3>
            <ul className="service-deliverables">
              <li>Production-ready app architecture that can handle real merchants.</li>
              <li>Clear instrumentation for usage, performance, and error tracking.</li>
              <li>A roadmap and release discipline beyond v1.</li>
            </ul>
            <div className="service-tech">
              <p>
                <strong>Typical timeline:</strong> 8–16 weeks from concept to initial launch,
                followed by ongoing iterations.
              </p>
            </div>
          </div>
        </section>

        <section className="service-detail-section">
          <div className="container">
            <h2>Pricing & Analytics Systems</h2>
            <p className="service-description">
              Data models, reporting, and experimentation infrastructure that connect what you see
              in Shopify and your bank account to the levers you can actually pull.
            </p>
            <p className="service-who">
              <strong>Who it&apos;s for:</strong> Operators and founders who need clear, trustworthy
              numbers to make pricing, inventory, and marketing decisions.
            </p>
            <h3>Outcomes</h3>
            <ul className="service-deliverables">
              <li>Analytics you can explain to a non-technical stakeholder.</li>
              <li>Repeatable reporting instead of one-off spreadsheet archaeology.</li>
              <li>Experimentation frameworks for testing pricing and offers.</li>
            </ul>
            <div className="service-tech">
              <p>
                <strong>Typical timeline:</strong> 4–8 weeks including discovery and iteration.
              </p>
            </div>
          </div>
        </section>

        <section className="service-detail-section alt-bg">
          <div className="container">
            <h2>Long-Term Infrastructure Partner</h2>
            <p className="service-description">
              Ongoing ownership of the systems we build: small, focused roadmaps instead of
              scattered, one-off tasks.
            </p>
            <p className="service-who">
              <strong>Who it&apos;s for:</strong> Teams that want a senior engineering partner
              embedded over months, not weeks, without hiring an entire internal team.
            </p>
            <h3>Outcomes</h3>
            <ul className="service-deliverables">
              <li>Consistent improvements instead of sporadic &quot;big bang&quot; projects.</li>
              <li>A single technical owner of the systems we&apos;ve built together.</li>
              <li>Predictable cadence for changes, reviews, and roadmap planning.</li>
            </ul>
            <div className="service-tech">
              <p>
                <strong>Typical timeline:</strong> 3–12 month retainers with a defined monthly
                capacity.
              </p>
            </div>
          </div>
        </section>

        <section className="process-section">
          <div className="container">
            <h2>Our Process</h2>
            <div className="process-steps">
              <div className="process-step">
                <div className="step-header">
                  <div className="step-number">1</div>
                  <h3>Discover</h3>
                </div>
                <p>We map the workflows, constraints, and systems that already exist.</p>
              </div>
              <div className="process-step">
                <div className="step-header">
                  <div className="step-number">2</div>
                  <h3>Architect</h3>
                </div>
                <p>We design the system and agree on the trade-offs before implementation.</p>
              </div>
              <div className="process-step">
                <div className="step-header">
                  <div className="step-number">3</div>
                  <h3>Build</h3>
                </div>
                <p>We implement clean, testable infrastructure that your team can own.</p>
              </div>
              <div className="process-step">
                <div className="step-header">
                  <div className="step-number">4</div>
                  <h3>Maintain</h3>
                </div>
                <p>We stay on as a partner to refine, extend, and support the system.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="faq-section">
          <div className="container">
            <h2>What We Don&apos;t Do</h2>
            <div className="faq-list">
              <div className="faq-item">
                <h3>Quick one-off patches</h3>
                <p>
                  We&apos;re not the right partner if you want a single bug fixed or a cosmetic
                  tweak with no context. We work on systems, not isolated pixels.
                </p>
              </div>
              <div className="faq-item">
                <h3>Cheap templated builds</h3>
                <p>
                  If the primary goal is &quot;get something live as cheaply as possible,&quot; a
                  theme marketplace or low-cost agency is a better fit.
                </p>
              </div>
              <div className="faq-item">
                <h3>Projects without a real business problem</h3>
                <p>
                  We say no to work where the value is unclear. If we can&apos;t articulate the
                  business problem together, we don&apos;t start building.
                </p>
              </div>
              <div className="faq-item">
                <h3>Multiple vendors, no ownership</h3>
                <p>
                  If your plan is to split responsibility for the same system across several
                  agencies, we&apos;re not a fit. We take ownership for the infrastructure we build.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="services-cta-final">
          <div className="container">
            <h2>Have a System in Mind?</h2>
            <p>
              If you have a concrete business problem and want a partner to architect and build
              the system around it, we&apos;d like to hear about it.
            </p>
            <a href="/contact" className="cta-primary">
              Apply to Work With Us
            </a>
          </div>
        </section>
      </main>
    </>
  );
}

export default Services;
